import { useQuery } from '@tanstack/react-query';
import { getTokenFromStorage } from '@/utils/auth';
import {
  getCampaignCollaborations,
  type ClientCampaignCollaboration,
  type ClientCampaignCollaborationsResponse,
} from '@/api/beatlyApi';
import { filterCampaignCollaborationsInviteAccepted } from '@/utils/clientCampaignCollaborationInviteAccepted';

/** GET collaborations: filtered list for Content Review plus full client-accepted list for Profiles address merge. */
export type CampaignCollaborationsQueryData = ClientCampaignCollaborationsResponse & {
  collaborationsClientAccepted: ClientCampaignCollaboration[];
};

export interface UseCampaignCollaborationsParams {
  campaignId: string | undefined;
  enabled?: boolean;
}

/** Same validity rule as drafts hook — API ids look like `123456-cpg`. */
function isValidCampaignId(id: string | undefined): boolean {
  return Boolean(id && id.length >= 8 && id.includes('-'));
}

/**
 * GET /app/v2/client/campaign/:campaignId/collaborations
 *
 * Do **not** gate on `user.is_client` here: if it were false/undefined in storage (stale shape),
 * this query would never run while `/drafts` still would — Content Review empty, Approval full.
 * The API returns `NotClientException` for non-clients (same as drafts).
 *
 * Returned `collaborations` are filtered to invite-accepted rows (`ONGOING` / `DONE`) only.
 * `collaborationsClientAccepted` is the unfiltered API list (client accepted curation; may include `INVITED`).
 */
export function useCampaignCollaborations({ campaignId, enabled = true }: UseCampaignCollaborationsParams) {
  const token = getTokenFromStorage();
  const shouldRun = Boolean(token && campaignId && isValidCampaignId(campaignId) && enabled);

  return useQuery({
    queryKey: ['client', 'campaign', 'collaborations', campaignId],
    queryFn: async () => getCampaignCollaborations(campaignId!, token!),
    select: (data: ClientCampaignCollaborationsResponse): CampaignCollaborationsQueryData => {
      const raw = data.collaborations;
      return {
        ...data,
        collaborations: filterCampaignCollaborationsInviteAccepted(raw),
        collaborationsClientAccepted: raw,
      };
    },
    enabled: shouldRun,
    retry: false,
  });
}
